import { useState } from 'react';
import styled from 'styled-components';
import { useAssistGeoNotesMutation } from '@shared/api';
import type { GeoNotesAssist } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import {
  AiBadge,
  ErrorText,
  Hint,
  LinkButton,
  Muted,
  Notice,
  Panel,
  PanelHead,
  PanelTitle,
  Row,
  Stack,
  useAction,
} from '@widgets/GeoSchemes';
import { useSettingsSaver, useWizard } from './wizard';

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Item = styled.li`
  container: geo-note-ai / inline-size;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const NoteText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
  text-wrap: pretty;
`;

/* На узкой карточке кнопка уходит под текст. */
const ItemAction = styled.div`
  @container geo-note-ai (max-width: 420px) {
    grid-column: 1 / -1;
  }
`;

const Rejected = styled(Muted)`
  font-size: 12.5px;
`;

const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * «Предложить примечания (ИИ)»: формулировки примечаний по данным схемы. Числа
 * в них программа сверяет с расчётом — не совпало, предложение отброшено. В
 * схему попадает только то, что человек добавил (в `settings.extra_notes`).
 */
export function NotesAssistPanel() {
  const { scheme, readOnly, goTo } = useWizard();
  const [ask] = useAssistGeoNotesMutation();
  const asking = useAction();
  const saver = useSettingsSaver();
  const [result, setResult] = useState<GeoNotesAssist | null>(null);
  const [added, setAdded] = useState(false);

  if (readOnly) return null;

  const existing = new Set(scheme.settings.extra_notes.map(norm));
  const hasVersion = scheme.versions.length > 0;

  async function suggest() {
    setAdded(false);
    const res = await asking.runValue(() => ask(scheme.id).unwrap(), 'Примечания не предложены.');
    if (res) setResult(res);
  }

  async function add(note: string) {
    const ok = await saver.save((cur) =>
      cur.extra_notes.some((n) => norm(n) === norm(note))
        ? cur
        : { ...cur, extra_notes: [...cur.extra_notes, note.trim()] },
    );
    if (ok) setAdded(true);
  }

  return (
    <Panel aria-labelledby="geo-notes-ai-title">
      <PanelHead>
        <PanelTitle id="geo-notes-ai-title">Предложения примечаний</PanelTitle>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={() => void suggest()}
          disabled={asking.busy || saver.busy}
        >
          {asking.busy ? 'Готовим…' : result ? 'Предложить заново' : 'Предложить примечания (ИИ)'}
        </Button>
      </PanelHead>
      <Stack $gap={3}>
        <Hint>
          ИИ-помощник предлагает формулировки примечаний по данным схемы. Числа в них программа
          сверяет с расчётом; на лист попадёт только то, что вы добавите.
          {!scheme.deviations && ' Лучше после расчёта отклонений (шаг 7).'}
        </Hint>
        {asking.error && <ErrorText role="alert">{asking.error}</ErrorText>}
        {result && (
          <>
            {result.notes.length === 0 ? (
              <Muted>{result.note ?? 'Предложений нет.'}</Muted>
            ) : (
              <>
                <Row $gap={2}>
                  <AiBadge />
                  <Muted>Предложений: {result.notes.length}</Muted>
                </Row>
                <List aria-label="Предложенные примечания">
                  {result.notes.map((n, i) => {
                    const inNotes = existing.has(norm(n));
                    return (
                      <Item key={`${i}-${n}`}>
                        <NoteText>{n}</NoteText>
                        <ItemAction>
                          <Button
                            type="button"
                            $variant="secondary"
                            $size="sm"
                            onClick={() => void add(n)}
                            disabled={saver.busy || inNotes}
                            aria-label={
                              inNotes ? `Уже в примечаниях: ${n}` : `Добавить в примечания: ${n}`
                            }
                          >
                            {inNotes ? 'В примечаниях' : 'Добавить в примечания'}
                          </Button>
                        </ItemAction>
                      </Item>
                    );
                  })}
                </List>
                {result.note && <Hint>{result.note}</Hint>}
              </>
            )}
            {result.rejected > 0 && (
              <Rejected>
                Отброшено {result.rejected} — в них были числа, которых нет в расчёте.
              </Rejected>
            )}
          </>
        )}
        {saver.error && <ErrorText role="alert">{saver.error}</ErrorText>}
        {added && (
          <Notice $tone="info" role="status">
            <span>
              Примечание сохранено в схеме.
              {hasVersion ? ' Сгенерируйте схему заново, чтобы оно попало на лист.' : ''}
            </span>
            <div>
              <LinkButton type="button" onClick={() => goTo(4)}>
                Все примечания — шаг 4 →
              </LinkButton>
            </div>
          </Notice>
        )}
      </Stack>
    </Panel>
  );
}
